<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow, parseISO, isValid } from 'date-fns';
import { useAlert } from 'dashboard/composables';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import { useCompaniesStore } from 'dashboard/stores/companies';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  domain: { type: String, default: '' },
  contactsCount: { type: Number, default: 0 },
  description: { type: String, default: '' },
  avatarUrl: { type: String, default: '' },
  updatedAt: { type: [String, Number], default: null },
  isExpanded: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle', 'showCompany', 'updated']);

const { t } = useI18n();
const companiesStore = useCompaniesStore();

const isUpdating = ref(false);
const editData = ref({
  name: props.name,
  domain: props.domain,
  description: props.description,
});

const onClickViewDetails = () => emit('showCompany', props.id);

const onClickExpand = () => {
  // Reset edit data when expanding
  if (!props.isExpanded) {
    editData.value = {
      name: props.name || '',
      domain: props.domain || '',
      description: props.description || '',
    };
  }
  emit('toggle');
};

const handleUpdate = async () => {
  isUpdating.value = true;
  try {
    await companiesStore.update(props.id, editData.value);
    useAlert(t('COMPANIES.FORM.UPDATE_SUCCESS'));
    emit('updated');
    emit('toggle'); // Close after update
  } catch (error) {
    useAlert(t('COMPANIES.FORM.UPDATE_ERROR'));
  } finally {
    isUpdating.value = false;
  }
};

const displayName = computed(() => props.name || t('COMPANIES.UNNAMED'));

const avatarSource = computed(() => props.avatarUrl || null);

const formattedUpdatedAt = computed(() => {
  if (!props.updatedAt) return '';
  try {
    const date = typeof props.updatedAt === 'string' ? parseISO(props.updatedAt) : new Date(props.updatedAt);
    if (!isValid(date)) return '';
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (e) {
    return '';
  }
});
</script>

<template>
  <CardLayout
    layout="row"
    class="cursor-pointer transition-all duration-200 hover:outline-n-brand hover:shadow-md"
    :class="{
      'outline-n-brand': isExpanded,
    }"
    @click="!isExpanded && onClickViewDetails()"
  >
    <div class="flex items-center justify-start flex-1 gap-4">
      <Avatar
        :username="displayName"
        :src="avatarSource"
        class="shrink-0"
        :name="name"
        :size="48"
        hide-offline-status
        rounded-full
      />
      <div class="flex flex-col gap-0.5 flex-1 min-w-0">
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1 min-w-0">
          <span class="text-base font-medium truncate text-n-slate-12">
            {{ displayName }}
          </span>
          <span
            v-if="domain && description"
            class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate"
          >
            <Icon icon="i-lucide-globe" size="size-3.5 text-n-slate-11" />
            <span class="truncate">{{ domain }}</span>
          </span>
        </div>
        <div class="flex items-center justify-between">
          <div class="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
            <span
              v-if="domain && !description"
              class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate"
            >
              <Icon icon="i-lucide-globe" size="size-3.5 text-n-slate-11" />
              <span class="truncate">{{ domain }}</span>
            </span>
            <span
              v-if="description"
              class="text-sm text-n-slate-11 truncate whitespace-nowrap max-w-[300px]"
            >
              {{ description }}
            </span>
            <div
              v-if="(description || domain) && contactsCount"
              class="w-px h-3 bg-n-slate-6"
            />
            <span
              v-if="contactsCount"
              class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate"
            >
              <Icon icon="i-lucide-users" size="size-3.5 text-n-slate-11" />
              {{ t('COMPANIES.CONTACTS_COUNT', { n: contactsCount }) }}
            </span>
          </div>
          <div class="flex items-center gap-3">
            <span
              v-if="updatedAt"
              class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 flex-shrink-0"
            >
              {{ formattedUpdatedAt }}
            </span>
            <Button
              :label="t('COMPANIES.VIEW_DETAILS')"
              variant="link"
              size="xs"
              class="opacity-0 group-hover/cardLayout:opacity-100 transition-opacity"
              @click.stop="onClickViewDetails"
            />
          </div>
        </div>
      </div>
    </div>

    <Button
      icon="i-lucide-chevron-down"
      variant="ghost"
      color="slate"
      size="xs"
      :class="{ 'rotate-180': isExpanded }"
      @click.stop="onClickExpand"
    />

    <template #after>
      <div
        class="transition-all duration-500 ease-in-out grid overflow-hidden"
        :class="
          isExpanded
            ? 'grid-rows-[1fr] opacity-100'
            : 'grid-rows-[0fr] opacity-0'
        "
      >
        <div class="overflow-hidden">
          <div class="flex flex-col gap-4 p-6 border-t border-n-strong">
            <div class="text-sm font-medium text-n-slate-12 mb-2">
              {{ t('COMPANIES.QUICK_EDIT_TITLE') }}
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                v-model="editData.name"
                :label="t('COMPANIES.FORM.NAME_LABEL')"
                :placeholder="t('COMPANIES.FORM.NAME_PLACEHOLDER')"
              />
              <Input
                v-model="editData.domain"
                :label="t('COMPANIES.FORM.DOMAIN_LABEL')"
                :placeholder="t('COMPANIES.FORM.DOMAIN_PLACEHOLDER')"
              />
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('COMPANIES.FORM.DESCRIPTION_LABEL') }}
              </label>
              <Editor
                :model-value="editData.description || ''"
                :placeholder="t('COMPANIES.FORM.DESCRIPTION_PLACEHOLDER')"
                class="[&>div]:min-h-[100px]"
                @update:model-value="val => editData.description = val"
              />
            </div>
            <div class="flex justify-end gap-2">
              <Button
                :label="t('COMPANIES.CANCEL')"
                variant="ghost"
                size="sm"
                @click="onClickExpand"
              />
              <Button
                :label="t('COMPANIES.SAVE')"
                size="sm"
                :is-loading="isUpdating"
                @click="handleUpdate"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </CardLayout>
</template>
